import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Briefcase, Calendar, MapPin, DollarSign } from 'lucide-react';

interface ServiceDetailsSectionProps {
  serviceName: string;
  setServiceName: (v: string) => void;
  serviceCategory: string;
  setServiceCategory: (v: string) => void;
  scopeOfWork: string;
  setScopeOfWork: (v: string) => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  estimatedValue: number;
  setEstimatedValue: (v: number) => void;
  suggestedSupplier: string;
  setSuggestedSupplier: (v: string) => void;
  commercialTerms: string;
  setCommercialTerms: (v: string) => void;
  errors?: Record<string, string>;
}

export const ServiceDetailsSection: React.FC<ServiceDetailsSectionProps> = ({
  serviceName,
  setServiceName,
  serviceCategory,
  setServiceCategory,
  scopeOfWork,
  setScopeOfWork,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  location,
  setLocation,
  estimatedValue,
  setEstimatedValue,
  suggestedSupplier,
  setSuggestedSupplier,
  commercialTerms,
  setCommercialTerms,
  errors = {},
}) => {
  return (
    <Card className="border border-border shadow-sm">
      <CardHeader className="pb-3 border-b border-border/50">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-primary" />
          <span>Service Contract & Scope of Work Details</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Service Name & Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              Service Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="e.g. Enterprise Cloud Security Penetration Testing"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
            />
            {errors.serviceName && <p className="text-[11px] text-destructive">{errors.serviceName}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Service Category</label>
            <select
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary text-xs"
              value={serviceCategory}
              onChange={(e) => setServiceCategory(e.target.value)}
            >
              <option value="ITServices">IT Consulting & Cybersecurity</option>
              <option value="ProfessionalServices">Legal & Financial Advisory</option>
              <option value="Logistics">Facilities & Maintenance Services</option>
              <option value="MRO">Equipment Maintenance Contract</option>
            </select>
          </div>
        </div>

        {/* Scope of Work */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground flex items-center justify-between">
            <span className="flex items-center gap-1">
              Detailed Scope of Work (SOW) <span className="text-destructive">*</span>
            </span>
            <span className="text-[11px] text-foreground-secondary font-mono">Min 20 chars</span>
          </label>
          <textarea
            className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary min-h-[90px] text-xs"
            placeholder="Define deliverables, SLA benchmarks, milestone sign-offs, and technical requirements..."
            value={scopeOfWork}
            onChange={(e) => setScopeOfWork(e.target.value)}
          />
          {errors.scopeOfWork && <p className="text-[11px] text-destructive">{errors.scopeOfWork}</p>}
        </div>

        {/* Dates & Location */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-primary" /> Start Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono text-xs"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-primary" /> End Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono text-xs"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-primary" /> Performance Location
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary text-xs"
              placeholder="e.g. Austin Tech Campus / Remote"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
        </div>

        {/* Commercial Terms & Suggested Supplier */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-border/40">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5 text-primary" /> Estimated Value ($)
            </label>
            <input
              type="number"
              min="0"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono text-xs"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(Math.max(0, parseFloat(e.target.value) || 0))}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Suggested Supplier</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary text-xs"
              placeholder="e.g. CyberSec Audit Group LLC"
              value={suggestedSupplier}
              onChange={(e) => setSuggestedSupplier(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Commercial Terms</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary text-xs"
              placeholder="e.g. Net 30 Days upon milestone sign-off"
              value={commercialTerms}
              onChange={(e) => setCommercialTerms(e.target.value)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
